import React, { useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router';
import _flatMap from 'lodash/flatMap';
import _get from 'lodash/get';
import { useData } from 'resift';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { Theme } from 'styles/ThemeTyping';
import makeGetUserFieldsFetch from 'fetches/makeGetUserFieldsFetch';
import isFieldTextCollection from 'helpers/isFieldTextCollection';
import isFieldValidConfigOption from 'helpers/isFieldValidConfigOption';
import parentFieldIsObjectArray from 'helpers/parentFieldIsObjectArray';
import getComparisonOptions from 'pages/RolesAndPermissions/helpers/getComparisonOptions';
import getInputType from 'pages/RolesAndPermissions/helpers/getInputType';
import {
  CONTENT_TYPE_FIELD,
  CONTENT_TYPE_COMPARISON,
  CONTENT_TYPE_VALUE,
  CONTENT_TYPE_FIELD_FOR_SAME_ATTR,
  VALID_FIELD_TYPES,
  RELATIVE_FILTERS,
  BACKDROP_Z_INDEX,
} from 'pages/RolesAndPermissions/consts';
import * as Sift from '@sift/types';
import * as Types from 'pages/RolesAndPermissions/typings';

import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Backdrop from '@material-ui/core/Backdrop';
import SearchableInput from './valueInput/SearchableInput';
import SelectInput from './valueInput/SelectInput';
import FilterValueInput from './valueInput/FilterValueInput';

interface Props {
  anchorEl: null | HTMLElement;
  filterIdInFocus: string;
  dropdownContentType: Types.DropdownContentType;
  filterGroups: Array<Types.FilterGroup>;
  onEditFilterField: (filterId: string, newField: Types.FilterField) => void;
  onEditSecondFilterField: (filterId: string, newField: Types.FilterField) => void;
  onEditFilterComparison: (filterId: string, newComparison: string) => void;
  onEditFilterValue: (filterId: string, newValue: string) => void;
  onBack: (currentType: Types.DropdownContentType) => void;
  onNext: (
    currentType: Types.DropdownContentType,
    options?: { fieldInFocus?: { displayName: string; type: string }; comparisonInFocus?: string },
  ) => void;
}

function FilterEditPopper({
  anchorEl,
  filterIdInFocus,
  dropdownContentType,
  filterGroups,
  onBack,
  onNext,
  onEditFilterField,
  onEditSecondFilterField,
  onEditFilterComparison,
  onEditFilterValue,
}: Props) {
  const classes = useStyles();
  const location = useLocation();
  const isOnManageMembersPage = location.pathname.includes('/manage-members');
  const getUserFields = makeGetUserFieldsFetch();
  const userFieldsData = useData(getUserFields);
  const userFields = _get(userFieldsData, 'results', []) as Array<Sift.Field>;
  const availableUserFields: Array<Types.FilterField> = userFields
    .filter(
      f =>
        isFieldValidConfigOption(f) &&
        !parentFieldIsObjectArray(f, userFields) &&
        (VALID_FIELD_TYPES.includes(f.type) || isFieldTextCollection(f)),
    )
    .map(f => {
      const fieldType = isFieldTextCollection(f) ? 'textCollection' : f.type;
      return { displayName: f.displayName, type: fieldType, objectKey: f.objectKey };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  const filterInFocus = filterGroups
    .flatMap(group => group.filters)
    .find(filter => filter.id === filterIdInFocus) as Types.Filter | undefined;
  const { field = { displayName: '', type: '' }, comparison = '', value = '' } =
    filterInFocus || {};
  const [fieldDraft, setFieldDraft] = useState<Types.FilterField>(field);
  const [comparisonDraft, setComparisonDraft] = useState<string>(comparison);
  const [valueDraft, setValueDraft] = useState<string>(value);
  const [fieldSearch, setFieldSearch] = useState<string>('');
  const [fieldForSameAttrSearch, setFieldForSameAttrSearch] = useState<string>('');
  const [valueInput, setValueInput] = useState<string>('');

  const valueInputType = getInputType(comparisonDraft, fieldDraft.type);

  const relativeFieldOptions = useMemo(() => {
    const list = RELATIVE_FILTERS.filter(option => {
      return option.displayName.toLowerCase().includes(fieldSearch.toLowerCase());
    });

    return list;
  }, [fieldSearch]);

  const attributeFieldOptions = useMemo(() => {
    const list = availableUserFields.filter(option => {
      return option.displayName.toLowerCase().includes(fieldSearch.toLowerCase());
    });

    return list;
  }, [fieldSearch]);

  const fieldOptionsForSameAttr = useMemo(() => {
    const list = availableUserFields.filter(option => {
      return option.displayName.toLowerCase().includes(fieldForSameAttrSearch.toLowerCase());
    });

    return list;
  }, [fieldForSameAttrSearch]);

  const comparisonOptions = useMemo(() => getComparisonOptions(fieldDraft.type || field.type), [
    fieldDraft.type,
    field.type,
  ]);

  const isValueContentDropdown = dropdownContentType === CONTENT_TYPE_VALUE;
  const isFieldContentDropdown = dropdownContentType === CONTENT_TYPE_FIELD;
  const isFieldForSameAttrContentDropdown =
    dropdownContentType === CONTENT_TYPE_FIELD_FOR_SAME_ATTR;

  const handleFieldSearch = useCallback(e => setFieldSearch(e.target.value), [setFieldSearch]);
  const handleFieldSearchForSameAttr = useCallback(e => setFieldForSameAttrSearch(e.target.value), [
    setFieldForSameAttrSearch,
  ]);

  const handleConfirm = () => {
    onNext(dropdownContentType);
    setFieldDraft({ displayName: '', type: '' });
    setComparisonDraft('');
    setValueDraft('');
    setFieldSearch('');
    setValueInput('');

    onEditFilterValue(filterIdInFocus, valueDraft);
  };

  const handleBack = () => {
    onBack(dropdownContentType);

    if (dropdownContentType === CONTENT_TYPE_FIELD) {
      onEditFilterField(filterIdInFocus, { displayName: '', type: '' });
    } else if (dropdownContentType === CONTENT_TYPE_COMPARISON) {
      onEditFilterComparison(filterIdInFocus, '');
      onEditFilterField(filterIdInFocus, { displayName: '', type: '' });
    } else if (dropdownContentType === CONTENT_TYPE_VALUE) {
      onEditFilterValue(filterIdInFocus, '');
      onEditFilterComparison(filterIdInFocus, '');
    }
  };

  const handleFieldSelect = (newField: Types.FilterField) => {
    setFieldDraft(newField);
    onEditFilterField(filterIdInFocus, newField);
    onNext(CONTENT_TYPE_FIELD, { fieldInFocus: newField });
  };

  const handleFieldSelectForSameAttr = (newField: Types.FilterField) => {
    onEditSecondFilterField(filterIdInFocus, newField);
    onNext(CONTENT_TYPE_FIELD_FOR_SAME_ATTR, { fieldInFocus: newField });
  };

  const handleComparisonSelect = (newComparison: string) => {
    setComparisonDraft(newComparison);
    onEditFilterComparison(filterIdInFocus, newComparison);
    onNext(CONTENT_TYPE_COMPARISON, { comparisonInFocus: newComparison });
  };

  const handleValueSelect = (newValue: string) => {
    setValueDraft(newValue);
    setValueInput(newValue);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    handleValueSelect(newValue);
  };

  return (
    <>
      <Backdrop
        // (note - pearl) Backdrop is applied so that everything underneath the Popper is not clickable
        open={!!anchorEl}
        className={classes.backdrop}
      />
      <Popper
        open={!!anchorEl}
        anchorEl={anchorEl}
        transition
        disablePortal
        className={classes.root}
        placement="bottom"
        modifiers={{
          flip: { enabled: false },
          preventOverflow: { enabled: false },
        }}
      >
        <Paper className={classes.dropdown}>
          {isFieldContentDropdown && (
            <SearchableInput
              onSearch={handleFieldSearch}
              options={isOnManageMembersPage ? attributeFieldOptions : relativeFieldOptions}
              secondaryOptions={isOnManageMembersPage ? undefined : attributeFieldOptions}
              sectionTitle={isOnManageMembersPage ? undefined : 'Relative Filters'}
              secondarySectionTitle={isOnManageMembersPage ? undefined : 'Attributes'}
              onClick={handleFieldSelect}
            />
          )}
          {isFieldForSameAttrContentDropdown && (
            <SearchableInput
              onSearch={handleFieldSearchForSameAttr}
              options={fieldOptionsForSameAttr}
              onClick={handleFieldSelectForSameAttr}
            />
          )}
          {dropdownContentType === CONTENT_TYPE_COMPARISON && (
            <SelectInput options={comparisonOptions} onSelect={handleComparisonSelect} />
          )}
          {isValueContentDropdown && (
            <FilterValueInput
              input={valueInput}
              inputType={valueInputType}
              fieldObjectKey={fieldDraft.objectKey || field.objectKey}
              onChange={handleValueChange}
              onSelect={handleValueSelect}
            />
          )}
          {!isFieldContentDropdown && <Divider className={classes.divider} />}

          {!isFieldContentDropdown && (
            <div
              className={classNames(
                classes.buttonContainer,
                !isValueContentDropdown && classes.centeredButton,
              )}
            >
              <Button onClick={handleBack}>Go Back</Button>
              {isValueContentDropdown && (
                <Button onClick={handleConfirm} className={classes.confirmButton} color="secondary">
                  Confirm
                </Button>
              )}
            </div>
          )}
        </Paper>
      </Popper>
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 271,
    zIndex: 2000,
    position: 'relative',
  },
  confirmButton: {
    zIndex: 999,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    height: 38,
  },
  centeredButton: {
    justifyContent: 'center',
  },
  dropdown: {
    padding: theme.spacing(),
    marginLeft: theme.spacing(5),
  },
  divider: {
    margin: 0,
  },
  backdrop: {
    zIndex: BACKDROP_Z_INDEX,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
}));

export default FilterEditPopper;
